import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { User, Role } from '../../../interfaces/user.interface';
import { EntityService } from '../../../services/entity.service';
import { NotificationService } from '../../../services/notification.service';

interface ServerError {
  errors?: { [key: string]: string[] };
  message?: string;
}

@Component({
  selector: 'app-edit-user-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  template: `
    <div class="modal-header">
      <h4 class="modal-title">{{user ? 'Modifier' : 'Ajouter'}} un utilisateur</h4>
      <button type="button" class="btn-close" (click)="activeModal.dismiss()"></button>
    </div>
    <div class="modal-body">
      <form [formGroup]="userForm">
        <div class="alert alert-danger" *ngIf="serverError?.message">
          {{ serverError.message }}
        </div>

        <div class="row">
          <div class="col-md-4 mb-3">
            <label class="form-label">Prénom</label>
            <input type="text" class="form-control" formControlName="prenom"
              [class.is-invalid]="(userForm.get('prenom')?.touched && userForm.get('prenom')?.invalid) || serverError?.errors?.['prenom']">
            <div class="invalid-feedback" *ngIf="userForm.get('prenom')?.touched && userForm.get('prenom')?.errors">
              Le prénom est requis
            </div>
            <div class="invalid-feedback" *ngIf="serverError?.errors?.['prenom']">
              {{ serverError?.errors?.['prenom']?.[0] }}
            </div>
          </div>
          <div class="col-md-4 mb-3">
            <label class="form-label">Nom</label>
            <input type="text" class="form-control" formControlName="nom"
              [class.is-invalid]="(userForm.get('nom')?.touched && userForm.get('nom')?.invalid) || serverError?.errors?.['nom']">
            <div class="invalid-feedback" *ngIf="userForm.get('nom')?.touched && userForm.get('nom')?.errors">
              Le nom est requis
            </div>
            <div class="invalid-feedback" *ngIf="serverError?.errors?.['nom']">
              {{ serverError?.errors?.['nom']?.[0] }}
            </div>
          </div>
          <div class="col-md-4 mb-3">
            <label class="form-label">Post-nom</label>
            <input type="text" class="form-control" formControlName="postnom">
          </div>
        </div>

        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="form-label">Email</label>
            <input type="email" class="form-control" formControlName="email"
              [class.is-invalid]="(userForm.get('email')?.touched && userForm.get('email')?.invalid) || serverError?.errors?.['email']">
            <div class="invalid-feedback" *ngIf="userForm.get('email')?.touched && userForm.get('email')?.errors">
              <span *ngIf="userForm.get('email')?.errors?.['required']">L'email est requis</span>
              <span *ngIf="userForm.get('email')?.errors?.['email']">Format d'email invalide</span>
            </div>
            <div class="invalid-feedback" *ngIf="serverError?.errors?.['email']">
              {{ serverError?.errors?.['email']?.[0] }}
            </div>
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">Téléphone</label>
            <input type="tel" class="form-control" formControlName="telephone"
              [class.is-invalid]="(userForm.get('telephone')?.touched && userForm.get('telephone')?.invalid) || serverError?.errors?.['telephone']">
            <div class="invalid-feedback" *ngIf="userForm.get('telephone')?.touched && userForm.get('telephone')?.errors">
              Le numéro de téléphone est requis
            </div>
            <div class="invalid-feedback" *ngIf="serverError?.errors?.['telephone']">
              {{ serverError?.errors?.['telephone']?.[0] }}
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="form-label">Rôles</label>
            <div class="loader-container" *ngIf="loadingRoles">
              <div class="spinner-border spinner-border-sm text-primary" role="status">
                <span class="visually-hidden">Chargement...</span>
              </div>
              <span class="ms-2">Chargement des rôles...</span>
            </div>
            <div class="roles-grid" *ngIf="!loadingRoles">
              <div *ngFor="let role of roles" class="role-item">
                <input type="checkbox" 
                  [id]="'role_' + role.id"
                  [checked]="isRoleSelected(role.id)"
                  (change)="toggleRole(role.id)">
                <label [for]="'role_' + role.id">{{role.display_name}}</label>
              </div>
            </div>
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">Statut</label>
            <select class="form-select" formControlName="status">
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
            </select>
          </div>
        </div>

        <div class="row" *ngIf="!user">
          <div class="col-md-6 mb-3">
            <label class="form-label">Mot de passe</label>
            <input type="password" class="form-control" formControlName="password"
              [class.is-invalid]="userForm.get('password')?.touched && userForm.get('password')?.invalid">
            <div class="invalid-feedback" *ngIf="userForm.get('password')?.touched && userForm.get('password')?.errors">
              <span *ngIf="userForm.get('password')?.errors?.['required']">Le mot de passe est requis</span>
              <span *ngIf="userForm.get('password')?.errors?.['minlength']">Le mot de passe doit contenir au moins 6 caractères</span>
            </div>
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">Confirmer le mot de passe</label>
            <input type="password" class="form-control" formControlName="password_confirmation"
              [class.is-invalid]="userForm.get('password_confirmation')?.touched && 
                (userForm.get('password_confirmation')?.invalid || userForm.errors?.['passwordMismatch'])">
            <div class="invalid-feedback" *ngIf="userForm.get('password_confirmation')?.touched && 
              (userForm.get('password_confirmation')?.invalid || userForm.errors?.['passwordMismatch'])">
              <span *ngIf="userForm.get('password_confirmation')?.errors?.['required']">La confirmation est requise</span>
              <span *ngIf="userForm.errors?.['passwordMismatch']">Les mots de passe ne correspondent pas</span>
            </div>
          </div>
        </div>
      </form>
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" (click)="activeModal.dismiss()">Annuler</button>
      <button type="button" class="btn btn-primary" (click)="save()" 
        [disabled]="!user && !userForm.valid || isSaving">
        {{isSaving ? 'Enregistrement...' : (user ? 'Mettre à jour' : 'Ajouter')}}
      </button>
    </div>
  `,
  styles: [`
    .modal-body {
      max-height: 70vh;
      overflow-y: auto;
    }
  `]
})
export class EditUserModalComponent implements OnInit {
  @Input() user?: User;
  userForm: FormGroup;
  roles: Role[] = [];
  selectedRoles: number[] = [];
  loadingRoles = false;
  isSaving = false;
  serverError: ServerError | null = null;

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private entityService: EntityService,
    private notificationService: NotificationService
  ) {
    this.userForm = this.fb.group({
      prenom: ['', Validators.required],
      nom: ['', Validators.required],
      name: [''],
      postnom: [''],
      email: ['', [Validators.required, Validators.email]],
      telephone: ['', Validators.required],
      status: ['active'],
      rang: ['admin'],
      password: [''],
      password_confirmation: ['']
    }, {
      validators: this.passwordMatchValidator
    });

    this.userForm.get('nom')?.valueChanges.subscribe(value => {
      this.userForm.patchValue({ name: value }, { emitEvent: false });
    });
  }

  ngOnInit() {
    this.loadRoles();
    if (this.user) {
      this.userForm.patchValue({
        ...this.user,
        name: this.user.nom
      });
      this.selectedRoles = this.user.roles?.map(role => role.id) || [];
    } else {
      this.userForm.get('password')?.setValidators([Validators.required, Validators.minLength(6)]);
      this.userForm.get('password_confirmation')?.setValidators([Validators.required]);
      this.userForm.get('password')?.updateValueAndValidity();
      this.userForm.get('password_confirmation')?.updateValueAndValidity();
    }
  }

  loadRoles() {
    this.loadingRoles = true;
    this.entityService.getDatas('/roles').subscribe({
      next: (response: any) => {
        this.roles = response;
        this.loadingRoles = false;
      },
      error: (error) => {
        console.error('Erreur lors du chargement des rôles:', error);
        this.loadingRoles = false;
        this.notificationService.error('Erreur lors du chargement des rôles');
      }
    });
  }

  passwordMatchValidator(group: FormGroup) {
    const password = group.get('password');
    const confirmPassword = group.get('password_confirmation');
    
    if (!password || !confirmPassword) return null;
    
    return password.value === confirmPassword.value ? null : { passwordMismatch: true };
  }

  isRoleSelected(roleId: number): boolean {
    return this.selectedRoles.includes(roleId);
  }

  toggleRole(roleId: number) {
    const index = this.selectedRoles.indexOf(roleId);
    if (index === -1) {
      this.selectedRoles.push(roleId);
    } else {
      this.selectedRoles.splice(index, 1);
    }
  }

  save() {
    this.serverError = null;
    Object.keys(this.userForm.controls).forEach(key => {
      const control = this.userForm.get(key);
      control?.markAsTouched();
    });

    if (this.userForm.valid) {
      this.isSaving = true;
      const formData = {
        ...this.userForm.value,
        roles: this.selectedRoles
      };

      if (this.user) {
        if (!formData.password) {
          delete formData.password;
          delete formData.password_confirmation;
        }
      }

      const request = this.user 
        ? this.entityService.updateDataWithPost('/admin/admins', this.user.id, formData)
        : this.entityService.saveData('/admin/create-admin', formData);

      request.subscribe({
        next: (response) => {
          this.isSaving = false;
          this.notificationService.success(
            this.user ? 'Utilisateur mis à jour avec succès' : 'Utilisateur créé avec succès'
          );
          this.activeModal.dismiss(response);
        },
        error: (error) => {
          this.isSaving = false;
          this.serverError = error.error;
          
          if (this.serverError?.errors) {
            Object.keys(this.serverError.errors).forEach(key => {
              const control = this.userForm.get(key);
              if (control) {
                control.setErrors({ serverError: true });
              }
            });
          }
          
          this.notificationService.error(
            this.serverError?.message || 'Une erreur est survenue lors de l\'enregistrement'
          );
        }
      });
    }
  }
} 